import React, { PropsWithChildren, ReactNode, useEffect } from "react";

export type NoticeState = { type: "ok" | "warn" | "error"; message: string } | null;

export type PillTabItem<T extends string> = {
  key: T;
  label: string;
  badge?: string;
};

type PageTitleBarProps = {
  title: string;
  description?: string;
  right?: ReactNode;
};

type MetaStateBarProps = {
  items: Array<{ key: string; value: string; tone?: "default" | "ok" | "warn" | "error" | "dirty" }>;
};

type PillTabBarProps<T extends string> = {
  active: T;
  items: Array<PillTabItem<T>>;
  onChange: (next: T) => void;
};

type PagerProps = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

type ModalProps = PropsWithChildren<{
  open: boolean;
  title: string;
  onClose: () => void;
  width?: number;
  actions?: ReactNode;
}>;

// 페이지 제목/설명/우측 액션 슬롯을 표시하는 공통 헤더다.
export function PageTitleBar({ title, description, right }: PageTitleBarProps) {
  return (
    <div className="cm-page-head">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {right ? <div>{right}</div> : null}
    </div>
  );
}

// 메타 상태 배지를 가로 바 형태로 렌더링한다.
export function MetaStateBar({ items }: MetaStateBarProps) {
  return (
    <div className="cm-meta-bar">
      {items.map((item) => (
        <span className={`cm-pill ${item.tone && item.tone !== "default" ? item.tone : ""}`.trim()} key={item.key}>
          {item.value}
        </span>
      ))}
    </div>
  );
}

// 공통 알림 배너를 tone별 스타일로 렌더링한다.
export function NoticeBanner({ notice }: { notice: NoticeState }) {
  if (!notice) return null;
  const toneClass = notice.type === "error" ? "error" : notice.type === "warn" ? "warn" : "";
  return <div className={`cm-notice ${toneClass}`.trim()}>{notice.message}</div>;
}

// 정보성 안내 문구를 카드형 배너로 표시한다.
export function InfoBanner({ children }: PropsWithChildren) {
  return <div className="cm-info">{children}</div>;
}

// 탭 목록을 pill 버튼으로 렌더링하고 현재 탭 변경을 전달한다.
export function PillTabBar<T extends string>({ active, items, onChange }: PillTabBarProps<T>) {
  return (
    <div className="cm-tabs" role="tablist">
      {items.map((item) => (
        <button
          className={`cm-tab ${active === item.key ? "active" : ""}`.trim()}
          key={item.key}
          onClick={() => onChange(item.key)}
          role="tab"
          type="button"
        >
          {item.label}
          {item.badge ? ` (${item.badge})` : ""}
        </button>
      ))}
    </div>
  );
}

// 제목/액션/본문 슬롯을 갖는 공통 섹션 카드다.
export function SectionCard({ title, children, actions, className }: PropsWithChildren<{ title: ReactNode; actions?: ReactNode; className?: string }>) {
  return (
    <section className={`cm-section-card ${className || ""}`.trim()}>
      <div className="cm-page-head" style={{ marginBottom: 8 }}>
        <h3>{title}</h3>
        {actions ? <div className="cm-actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

// 비동기 로딩 상태를 한 줄 텍스트로 표시한다.
export function LoadingLine({ message }: { message: string }) {
  return <div className="cm-loading-line">{message}</div>;
}

// 페이지 이동(Prev/Next/직접 입력) 공통 UI를 제공한다.
export function Pager({ page, totalPages, onPageChange }: PagerProps) {
  const safeTotal = Math.max(1, totalPages || 1);
  const safePage = Math.min(Math.max(1, page || 1), safeTotal);

  return (
    <div className="cm-pager">
      <button className="cm-btn cm-btn-secondary" disabled={safePage <= 1} onClick={() => onPageChange(safePage - 1)} type="button">
        Prev
      </button>
      <span className="cm-pill">
        {safePage} / {safeTotal}
      </span>
      <input
        className="cm-input cm-pager-input"
        min={1}
        max={safeTotal}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          const value = Number((event.target as HTMLInputElement).value);
          if (!Number.isFinite(value)) return;
          onPageChange(Math.min(Math.max(1, Math.floor(value)), safeTotal));
        }}
        type="number"
        defaultValue={safePage}
      />
      <button className="cm-btn cm-btn-secondary" disabled={safePage >= safeTotal} onClick={() => onPageChange(safePage + 1)} type="button">
        Next
      </button>
    </div>
  );
}

/**
 * ESC/배경 클릭 닫기 동작을 포함한 공통 모달.
 * 열림 상태에서는 body 스크롤을 잠가 배경 스크롤을 방지한다.
 */
export function Modal({ open, title, onClose, width, actions, children }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    // ESC 키 입력 시 모달을 닫는다.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="cm-modal-backdrop" onMouseDown={onClose}>
      <div
        className="cm-modal"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={width ? { width: `min(${width}px, 100%)` } : undefined}
      >
        <div className="cm-modal-head">
          <h3 className="cm-modal-title">{title}</h3>
          <div className="cm-actions">
            {actions}
            <button className="cm-btn cm-btn-secondary" onClick={onClose} type="button">
              닫기
            </button>
          </div>
        </div>
        <div className="cm-modal-body">{children}</div>
      </div>
    </div>
  );
}
